/**
 * Annotation queue page — per `feature-spec.md` §3.3.
 *
 * Mounted at `/v4/go-lab/annotate/queue` under `AnnotateLayout`, which
 * owns the page chrome (H1 "Annotate" + subtitle + tab nav). This page
 * just renders its content sections:
 *   0. Header row with "Pick up next record" CTA (B14-FE) — self-claim
 *      next available record via `POST /records/next/start`; on success
 *      navigate to the flow page; on 204 toast an info copy.
 *   1. PipelineCountsStrip — global flow with em-dash placeholders.
 *   2. QueueStatStrip — two user-scoped cards; only "records in queue"
 *      pulls real data (from `useMyQueue().total`).
 *   3. QueueTable — 4 columns; row click → B9 annotation flow at
 *      `/v4/go-lab/annotate/queue/:recordId`.
 *   4. SnapshotHistoryPanel — capture + rollback affordances (B12-FE).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '../../../ui/button';
import {
  useCompletedToday,
  useMyQueue,
  useStartNextAvailable,
} from './hooks/useAnnotation';
import PipelineCountsStrip from './components/queue/PipelineCountsStrip';
import QueueStatStrip from './components/queue/QueueStatStrip';
import QueueTable from './components/queue/QueueTable';
import SnapshotHistoryPanel from './components/snapshots/SnapshotHistoryPanel';

const PAGE_SIZE = 50;

const SkeletonTable: React.FC = () => (
  <div className="space-y-2" data-testid="queue-loading">
    {[1, 2, 3, 4, 5].map((i) => (
      <div
        key={i}
        className="h-12 animate-pulse rounded-md border border-border/70 bg-card"
      />
    ))}
  </div>
);

const ErrorPanel: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div
    className="flex h-[40vh] flex-col items-center justify-center text-center"
    data-testid="queue-error"
  >
    <p className="mb-4 text-muted-foreground">Failed to load the queue.</p>
    <Button variant="link" size="sm" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

const AnnotateQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useMyQueue({
    limit: PAGE_SIZE,
    offset: 0,
  });
  const { data: completedTodayData } = useCompletedToday();
  const startNext = useStartNextAvailable();

  const handlePickUpNext = async () => {
    try {
      const result = await startNext.mutateAsync();
      if (result === null) {
        toast.info('No records waiting for you right now.');
        return;
      }
      navigate(`/v4/go-lab/annotate/queue/${result.record.id}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to pick up next record.',
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button
          variant="secondary"
          size="sm"
          className="h-8 rounded-md border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs hover:bg-accent"
          onClick={handlePickUpNext}
          disabled={startNext.isPending}
          data-testid="pick-up-next"
        >
          {startNext.isPending ? 'Picking up…' : 'Pick up next record'}
        </Button>
      </div>

      <PipelineCountsStrip />

      <QueueStatStrip
        recordsInQueue={data?.total ?? null}
        completedToday={completedTodayData?.annotationSubmissions ?? null}
      />

      {isLoading ? (
        <SkeletonTable />
      ) : isError ? (
        <ErrorPanel onRetry={() => refetch()} />
      ) : (
        <QueueTable records={data?.items ?? []} />
      )}

      <SnapshotHistoryPanel />
    </div>
  );
};

export default AnnotateQueuePage;
